/**
 * The loading screen (Phase 8): byte progress from the loaders, the arena card, the mechs, a
 * control hint from the account's bindings, and Deploy. The click is the first gesture, so it
 * unlocks audio and takes the pointer lock as the match starts; online the room's own waiting
 * notice follows once the pilot is in.
 */
import { ARENA_DESCRIPTIONS, ARENA_NAMES, isArenaId } from '@steelwake/shared/arena';
import { getMech } from '@steelwake/shared/data';
import { bindingLabel, type Bindings } from '@steelwake/shared/progression';
import { Button, colors, fonts, radii, spacing } from '@steelwake/ui';

import type { GameStatus } from '../../game/Game.js';
import type { MatchDescriptor } from '../../game/match/driver.js';

export interface LoadingProps {
  status: GameStatus;
  descriptor: MatchDescriptor | null;
  bindings: Bindings;
  online: boolean;
  onDeploy: () => void;
}

function describe(status: GameStatus): string {
  switch (status.phase) {
    case 'probing':
      return 'Probing graphics backend…';
    case 'initialising':
      return `Starting ${status.message === 'webgl2' ? 'WebGL 2' : 'WebGPU'} renderer…`;
    case 'connecting':
      return status.message ?? 'Connecting…';
    case 'loading':
      return 'Loading arena and mechs…';
    case 'staged':
      return 'Ready to deploy';
    case 'ready':
      return 'ready';
    case 'error':
      return `Error: ${status.message ?? 'unknown'}`;
  }
}

function keys(bindings: Bindings, action: keyof Bindings): string {
  return bindings[action].map(bindingLabel).join(' / ');
}

export function Loading({ status, descriptor, bindings, online, onDeploy }: LoadingProps) {
  const progress = status.phase === 'staged' ? 1 : (status.progress ?? 0);
  const arena = descriptor && isArenaId(descriptor.arenaId) ? descriptor.arenaId : null;
  const staged = status.phase === 'staged';
  const error = status.phase === 'error';
  return (
    <div
      data-testid="loading-screen"
      data-phase={status.phase}
      style={{
        position: 'absolute',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        background: 'radial-gradient(ellipse at center, rgba(20,24,30,0.92) 0%, #0b0d10 100%)',
        fontFamily: fonts.ui,
        color: colors.text,
        zIndex: 5,
      }}
    >
      <div style={{ width: 'min(560px, 92vw)', display: 'grid', gap: spacing.md }}>
        <div>
          <div
            style={{
              fontSize: 12,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: colors.textMuted,
            }}
          >
            {online ? 'Online match' : 'Practice'}
          </div>
          <h1
            data-testid="loading-arena"
            style={{ margin: '4px 0 0', fontSize: 30, fontWeight: 700 }}
          >
            {arena ? ARENA_NAMES[arena] : 'Arena'}
          </h1>
          <p style={{ margin: '4px 0 0', color: colors.textMuted, fontSize: 14 }}>
            {arena ? ARENA_DESCRIPTIONS[arena] : 'Resolving the match…'}
          </p>
        </div>
        {descriptor ? (
          <div data-testid="loading-mechs" style={{ fontSize: 14 }}>
            <b>{getMech(descriptor.mechs[descriptor.mySlot]).name}</b>
            <span style={{ color: colors.textMuted }}> versus </span>
            <b>{getMech(descriptor.mechs[descriptor.mySlot === 0 ? 1 : 0]).name}</b>
          </div>
        ) : null}
        <div>
          <div
            data-testid="loading-progress"
            data-value={Math.round(progress * 100)}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress * 100)}
            style={{
              height: 6,
              background: 'rgba(255,255,255,0.12)',
              borderRadius: 3,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${String(Math.round(progress * 100))}%`,
                height: '100%',
                background: error ? colors.danger : colors.accent,
                transition: 'width 200ms linear',
              }}
            />
          </div>
          <div
            data-testid="loading-status"
            data-phase={status.phase}
            role="status"
            style={{ marginTop: 6, fontSize: 13, color: error ? colors.danger : colors.textMuted }}
          >
            {describe(status)}
          </div>
        </div>
        <dl
          data-testid="loading-controls"
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '4px 16px',
            margin: 0,
            fontSize: 13,
            color: colors.textMuted,
          }}
        >
          <dt>Move</dt>
          <dd style={{ margin: 0 }}>
            {[
              bindings.moveForward[0],
              bindings.strafeLeft[0],
              bindings.moveBack[0],
              bindings.strafeRight[0],
            ]
              .map((b) => (b === undefined ? '?' : bindingLabel(b)))
              .join(' ')}
          </dd>
          <dt>Boost · jump</dt>
          <dd style={{ margin: 0 }}>
            {keys(bindings, 'boost')} · {keys(bindings, 'jump')}
          </dd>
          <dt>Fire</dt>
          <dd style={{ margin: 0 }}>
            {keys(bindings, 'primary')} · {keys(bindings, 'secondary')}
          </dd>
          <dt>Ability · ultimate</dt>
          <dd style={{ margin: 0 }}>
            {keys(bindings, 'ability')} · {keys(bindings, 'ultimate')}
          </dd>
          <dt>Vent · melee</dt>
          <dd style={{ margin: 0 }}>
            {keys(bindings, 'vent')} · {keys(bindings, 'melee')}
          </dd>
        </dl>
        <div>
          <Button
            data-testid="deploy"
            disabled={!staged}
            onClick={onDeploy}
            style={{ padding: '12px 28px', fontSize: 16, borderRadius: radii.md }}
          >
            {staged ? 'Deploy' : 'Loading…'}
          </Button>
        </div>
      </div>
    </div>
  );
}
